import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AlertEvaluation, TenantAlert } from '@ww/shared';
import { ApiError, api, type AlertDetailResponse } from '../api';
import WeatherMap from '../components/WeatherMap';
import { useLive } from '../hooks/useLive';
import { I, Icon, KIND_ICON, typeIcon } from '../lib/icons';
import {
  NOTIFICATION_COLORS,
  NOTIFICATION_LABELS,
  errorMessage,
  kindStyle,
  watchAreaLabel,
} from '../lib/status';
import { fmtDateTime } from '../lib/time';

function safeDecode(v: string): string {
  if (!/%[0-9A-Fa-f]{2}/.test(v)) return v;
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
}

const box: CSSProperties = {
  padding: '14px 18px',
  background: '#0f1a2d',
  border: '1px solid #1e2c46',
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

export default function AlertDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const { tenant, lastUpdated, health } = useLive();
  const id = safeDecode(params.id ?? '');
  const [data, setData] = useState<AlertDetailResponse | null>(null);
  const [error, setError] = useState<{ msg: string; status: number } | null>(null);
  const tz = tenant?.timezone;

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const d = await api.alert(id);
      setData(d);
      setError(null);
    } catch (e) {
      setError({ msg: errorMessage(e), status: e instanceof ApiError ? e.status : 0 });
    }
  }, [id]);

  useEffect(() => {
    setData(null);
    setError(null);
  }, [id]);
  useEffect(() => {
    void load();
  }, [load, lastUpdated]);

  const mapAlerts = useMemo<TenantAlert[]>(
    () => (data?.alert ? [{ alert: data.alert, evaluation: data.evaluation as AlertEvaluation }] : []),
    [data],
  );

  const back = (
    <button
      type="button"
      onClick={() => navigate('/alerts')}
      style={{
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 32,
        padding: '0 8px 0 2px',
        border: 'none',
        background: 'transparent',
        color: '#8cc2ff',
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      <Icon d={I.chevL} size={16} />
      <span>All alerts</span>
    </button>
  );

  if (error && !data) {
    return (
      <div className="page">
        {back}
        <div className="err-box">
          {error.status === 404
            ? 'This alert was not found, or it does not affect this tenant.'
            : `Could not load alert: ${error.msg}`}
        </div>
      </div>
    );
  }
  if (!data?.alert) {
    return (
      <div className="page">
        {back}
        <div className="empty">Loading alert…</div>
      </div>
    );
  }

  const a = data.alert;
  const ev = data.evaluation;
  const k = kindStyle(a.event);
  const assets = Array.isArray(data.assets) ? data.assets : [];
  const notifications = Array.isArray(data.notifications) ? data.notifications : [];
  const affected = ev?.affectedAssets ?? [];
  const params_ = a.parameters ?? {};
  const wind = typeof params_.maxWindGustMph === 'number' ? `${params_.maxWindGustMph} mph` : '—';
  const hail = typeof params_.maxHailSizeIn === 'number' ? `${params_.maxHailSizeIn.toFixed(2)} in` : '—';
  const tor = typeof params_.tornadoDetection === 'string' && params_.tornadoDetection ? params_.tornadoDetection : '—';
  const precision = ev
    ? ev.precision === 'polygon'
      ? 'Alert polygon'
      : 'County/zone match (lower precision than a polygon)'
    : a.geometry
      ? 'Alert polygon'
      : 'Zone list only';
  const expiredNote = !a.active || (ev && ev.state !== 'active');

  return (
    <div className="page page-scroll" style={{ gap: 16 }}>
      {back}
      {expiredNote && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            background: '#16223a',
            border: '1px solid #2b3d5c',
            fontSize: 14,
            color: '#c2cedf',
            flexShrink: 0,
          }}
        >
          This alert is no longer active{ev?.state && ev.state !== 'active' ? ` (${ev.state})` : ''}. Showing the archived record.
        </div>
      )}
      {!ev && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: '#16223a', border: '1px solid #2b3d5c', fontSize: 14, color: '#c2cedf', flexShrink: 0 }}>
          This alert has no evaluation for the current tenant (county and monitored assets are not affected).
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0, flexWrap: 'wrap' }}>
        <span
          style={{
            width: 60,
            height: 60,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: k.tint,
            flexShrink: 0,
          }}
        >
          <Icon d={KIND_ICON[k.kind]} size={34} style={{ color: k.color }} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className="cond" style={{ margin: 0, fontSize: 36, fontWeight: 700, color: k.color }}>
              {a.event}
            </h1>
            {health?.stale && <span className="tag tag-stale" style={{ fontSize: 12 }}>STALE</span>}
          </div>
          <div style={{ fontSize: 14, color: '#9fb0c8', overflowWrap: 'anywhere' }}>
            {a.senderName || 'NWS'} ·{' '}
            <span className="mono" style={{ fontSize: 12.5 }}>
              {a.nwsId}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {(
            [
              ['Severity', a.severity],
              ['Urgency', a.urgency],
              ['Certainty', a.certainty],
            ] as const
          ).map(([key, v]) => (
            <div
              key={key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                padding: '8px 14px',
                borderRadius: 8,
                background: '#0f1a2d',
                border: '1px solid #1e2c46',
              }}
            >
              <span className="lbl" style={{ fontSize: 11 }}>
                {key}
              </span>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{v || '—'}</span>
            </div>
          ))}
        </div>
      </div>

      <div
        className="metric-strip"
        style={{
          gap: 1,
          background: '#1e2c46',
          border: '1px solid #1e2c46',
          borderRadius: 10,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {(
          [
            ['Sent', a.sent],
            ['Effective', a.effective],
            ['Onset', a.onset],
            ['Expires', a.expires],
            ['Ends', a.ends],
          ] as const
        ).map(([key, v]) => (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 14px', background: '#0f1a2d' }}>
            <span className="lbl" style={{ fontSize: 11 }}>
              {key}
            </span>
            <span className="mono" style={{ fontSize: 16 }}>
              {fmtDateTime(v, tz)}
            </span>
          </div>
        ))}
      </div>

      <div className="detail-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <div style={{ ...box, padding: '16px 18px', gap: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4 }}>{a.headline || a.event}</div>
            <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#d3ddeb', whiteSpace: 'pre-line' }}>
              {a.description || 'No description provided.'}
            </div>
          </div>
          {a.instruction && (
            <div style={{ ...box, background: '#3a1419', border: '1px solid #6e2a31', gap: 6 }}>
              <span className="lbl" style={{ color: '#ffb3b7' }}>
                Instruction
              </span>
              <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.45, color: '#fff', whiteSpace: 'pre-line' }}>
                {a.instruction}
              </span>
            </div>
          )}
          <div className="grid-2" style={{ gap: 14 }}>
            <div style={box}>
              <span className="lbl">Hazard parameters</span>
              {(
                [
                  ['Max wind gust', wind],
                  ['Max hail size', hail],
                  ['Tornado detection', tor],
                ] as const
              ).map(([key, v]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 14 }}>
                  <span style={{ color: '#9fb0c8' }}>{key}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={box}>
              <span className="lbl">Affected zones</span>
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                {(a.affectedZones ?? []).length ? a.affectedZones.join(' · ') : '—'}
              </span>
              <span style={{ fontSize: 13, color: '#9fb0c8' }}>Geometry: {precision}</span>
              {a.areaDesc && <span style={{ fontSize: 13, color: '#9fb0c8' }}>{a.areaDesc}</span>}
              {watchAreaLabel(ev) && (
                <span style={{ fontSize: 13, color: '#ffc9a0' }}>Via extra location: {watchAreaLabel(ev)}</span>
              )}
            </div>
          </div>
          {ev && ev.matchedRules?.length > 0 && (
            <div style={box}>
              <span className="lbl">
                Matched rules{' '}
                {!ev.qualifies
                  ? '· dashboard only'
                  : ev.smsEligible === false
                    ? '· dashboard and sound only (SMS is off for this location)'
                    : '· notifies staff'}
              </span>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6, color: '#d3ddeb' }}>
                {ev.matchedRules.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <div
            style={{
              position: 'relative',
              height: 340,
              background: '#0b1526',
              border: '1px solid #1e2c46',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <WeatherMap tenant={tenant} alerts={mapAlerts} assets={assets} fitToAlert={a.nwsId} highlightAlertId={a.nwsId} compact />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {a.url && (
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary btn-sm hov"
                style={{ textDecoration: 'none' }}
              >
                Official NWS product <Icon d={I.ext} size={14} />
              </a>
            )}
          </div>

          <div style={box}>
            <span className="lbl">Monitored assets affected</span>
            {affected.length === 0 && (
              <span style={{ fontSize: 14, color: '#9fb0c8' }}>
                {ev ? 'No enabled assets inside the polygon or within their alert radius.' : 'Not evaluated for this tenant.'}
              </span>
            )}
            {affected.map((x) => {
              const asset = assets.find((s) => s._id === x.assetId);
              const color = x.relation === 'inside' ? '#ff8f93' : x.relation === 'proximity' ? '#ffa857' : '#f7cf4f';
              const note =
                x.relation === 'inside'
                  ? 'Inside polygon'
                  : x.relation === 'proximity'
                    ? `${(x.distanceMiles ?? 0).toFixed(1)} mi${asset ? ` · radius ${asset.alertRadiusMiles} mi` : ''} · Within radius`
                    : 'County / zone match';
              return (
                <div key={x.assetId} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                  <Icon d={typeIcon(x.type)} size={18} style={{ color }} />
                  <span style={{ flexGrow: 1, minWidth: 0 }}>{x.name}</span>
                  <span style={{ color, flexShrink: 0 }}>{note}</span>
                </div>
              );
            })}
          </div>

          <div style={{ ...box, gap: 10 }}>
            <span className="lbl">Notification history</span>
            {ev && (
              <span style={{ fontSize: 13, color: '#9fb0c8' }}>
                State:{' '}
                <span style={{ color: NOTIFICATION_COLORS[ev.notificationState] ?? '#9fb0c8' }}>
                  {NOTIFICATION_LABELS[ev.notificationState] ?? ev.notificationState}
                </span>
                {ev.lastNotifiedAt ? ` · last ${fmtDateTime(ev.lastNotifiedAt, tz)}` : ''}
              </span>
            )}
            {notifications.length === 0 && <span style={{ fontSize: 14, color: '#9fb0c8' }}>No notifications recorded.</span>}
            {notifications.map((n) => (
              <div key={n._id} style={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr)', gap: 10, fontSize: 13.5 }}>
                <span className="mono" style={{ color: '#9fb0c8', fontSize: 12.5 }}>
                  {fmtDateTime(n.sentAt, tz, true)}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <span>
                    <span style={{ color: NOTIFICATION_COLORS[n.result] ?? '#9fb0c8', fontWeight: 600 }}>
                      {NOTIFICATION_LABELS[n.result] ?? n.result}
                    </span>
                    {n.reason ? ` · ${n.reason}` : ''}
                  </span>
                  {n.payload?.message && <span style={{ color: '#d3ddeb', overflowWrap: 'anywhere' }}>{n.payload.message}</span>}
                  <span style={{ color: '#9fb0c8' }}>
                    {(n.recipientGroup ?? []).length ? n.recipientGroup.join(' · ') : '—'}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
